/**
 * Behaviour for the "Collection rows" section.
 * - Shows previous/next buttons for a row only when its product strip overflows.
 * - Lets a mouse drag the strip sideways (touch screens already swipe natively).
 * - Scrolls smoothly to a row from the list under the heading and marks the row in view.
 * The strips are plain CSS scrollers and the list uses normal anchors, so the page
 * works without this module.
 */
class CollectionRows extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {IntersectionObserver | undefined} */
  #rowObserver;

  /** @type {Map<Element, number>} */
  #frames = new Map();

  connectedCallback() {
    this.#teardown();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const row = entry.target.closest('[data-row]');
        if (row instanceof HTMLElement) this.#updateArrows(row);
      }
    });

    for (const row of this.rows) {
      const track = row.querySelector('[data-track]');
      if (!(track instanceof HTMLElement)) continue;

      track.addEventListener('scroll', () => this.#scheduleUpdate(row), { passive: true, signal });
      row
        .querySelector('[data-previous]')
        ?.addEventListener('click', () => this.#scrollRow(row, -1), { signal });
      row.querySelector('[data-next]')?.addEventListener('click', () => this.#scrollRow(row, 1), { signal });

      this.#setupLoop(row, track, signal);
      this.#bindDrag(row, track, signal);
      this.#resizeObserver.observe(track);
      this.#updateArrows(row);
    }

    const links = this.links;
    if (links.length === 0) return;

    for (const link of links) {
      link.addEventListener('click', (event) => this.#handleLinkClick(event, link), { signal });
    }

    // The row crossing a line a little above the middle of the screen is the current one.
    this.#rowObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setCurrent(entry.target.id);
        }
      },
      { rootMargin: '-35% 0px -60% 0px' }
    );

    for (const row of this.rows) this.#rowObserver.observe(row);
  }

  disconnectedCallback() {
    this.#teardown();
  }

  /** @returns {HTMLElement[]} */
  get rows() {
    return Array.from(this.querySelectorAll('[data-row]'));
  }

  /** @returns {HTMLAnchorElement[]} */
  get links() {
    return Array.from(this.querySelectorAll('a[data-row-link]'));
  }

  #teardown() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#rowObserver?.disconnect();
    for (const frame of this.#frames.values()) cancelAnimationFrame(frame);
    this.#frames.clear();
  }

  /** @param {HTMLElement} row */
  #scheduleUpdate(row) {
    cancelAnimationFrame(this.#frames.get(row) ?? 0);
    this.#frames.set(
      row,
      requestAnimationFrame(() => this.#updateArrows(row))
    );
  }

  /**
   * Shows the buttons when the strip can scroll and disables the one at an end.
   * @param {HTMLElement} row
   */
  #updateArrows(row) {
    const track = row.querySelector('[data-track]');
    const arrows = row.querySelector('[data-arrows]');
    if (!(track instanceof HTMLElement) || !(arrows instanceof HTMLElement)) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    arrows.hidden = maxScroll <= 1;
    track.toggleAttribute('data-draggable', maxScroll > 1);

    // scrollLeft is negative in right-to-left layouts, so compare its size.
    const position = Math.abs(track.scrollLeft);
    const previous = row.querySelector('[data-previous]');
    const next = row.querySelector('[data-next]');

    const looping = row.hasAttribute('data-looping');
    if (previous instanceof HTMLButtonElement) previous.disabled = !looping && position <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = !looping && position >= maxScroll - 1;
  }

  /**
   * Endless strip: one copy of the cards goes before and after the real ones. When the
   * strip comes to rest in a copy it jumps by exactly one set, which looks the same, so
   * it never reaches an end. Every visit starts on the first card.
   * @param {HTMLElement} row
   * @param {HTMLElement} track
   * @param {AbortSignal} signal
   */
  #setupLoop(row, track, signal) {
    let touched = false;
    const markTouched = () => (touched = true);
    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
      track.addEventListener(type, markTouched, { passive: true, signal });
    }
    row.querySelectorAll('[data-previous], [data-next]').forEach((button) =>
      button.addEventListener('click', markTouched, { signal })
    );

    /** @type {() => number} */
    let startPosition = () => 0;
    const goToStart = () => {
      if (!touched) track.scrollLeft = startPosition();
    };

    const originals = /** @type {HTMLElement[]} */ (Array.from(track.children));
    if (!row.hasAttribute('data-looping') && originals.length >= 2 && track.scrollWidth - track.clientWidth > 1) {
      const makeCopy = (/** @type {HTMLElement} */ item) => {
        const copy = /** @type {HTMLElement} */ (item.cloneNode(true));
        copy.setAttribute('data-clone', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) =>
          node.setAttribute('tabindex', '-1')
        );
        copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        return copy;
      };

      track.prepend(...originals.map(makeCopy));
      track.append(...originals.map(makeCopy));
      row.setAttribute('data-looping', '');

      const firstItem = /** @type {HTMLElement} */ (track.firstElementChild);
      const setWidth = () => originals[0].offsetLeft - firstItem.offsetLeft;
      startPosition = setWidth;

      let fingerDown = false;
      track.addEventListener('touchstart', () => (fingerDown = true), { passive: true, signal });
      track.addEventListener('touchend', () => (fingerDown = false), { passive: true, signal });
      track.addEventListener('touchcancel', () => (fingerDown = false), { passive: true, signal });

      const settle = () => {
        if (fingerDown || row.hasAttribute('data-dragging')) return;
        const width = setWidth();
        if (width <= 0) return;
        if (track.scrollLeft < width * 0.5) track.scrollLeft += width;
        else if (track.scrollLeft >= width * 1.5) track.scrollLeft -= width;
      };

      let timer = 0;
      track.addEventListener(
        'scroll',
        () => {
          clearTimeout(timer);
          timer = window.setTimeout(settle, 160);
        },
        { passive: true, signal }
      );
      if ('onscrollend' in window) track.addEventListener('scrollend', settle, { signal });
      window.addEventListener('resize', settle, { passive: true, signal });
    }

    goToStart();
    requestAnimationFrame(goToStart);
    window.addEventListener('load', goToStart, { signal });
    window.addEventListener('pageshow', goToStart, { signal });
  }

  /**
   * Mouse drag to scroll a strip. A drag that moves more than a few pixels does not
   * count as a click, so letting go over a product does not open it.
   * @param {HTMLElement} row
   * @param {HTMLElement} track
   * @param {AbortSignal} signal
   */
  #bindDrag(row, track, signal) {
    let pointerId = -1;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    // Inside a row, the photo carousel of each card should not start its own mouse drag.
    track.addEventListener(
      'mousedown',
      (event) => {
        if (event.button === 0 && this.#canDrag(track)) event.stopPropagation();
      },
      { capture: true, signal }
    );

    // Stop the browser's own image/link drag ghost.
    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    track.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || !this.#canDrag(track)) return;
        if (event.target instanceof Element && event.target.closest('button, input, select, textarea, label')) return;

        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = track.scrollLeft;
        moved = false;
      },
      { signal }
    );

    window.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerId !== pointerId) return;

        const distance = event.clientX - startX;
        if (!moved) {
          if (Math.abs(distance) < 6) return;
          moved = true;
          row.setAttribute('data-dragging', '');
        }

        event.preventDefault();
        track.scrollLeft = startScroll - distance;
      },
      { signal }
    );

    /** @param {PointerEvent} event */
    const stop = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = -1;
      if (!moved) return;

      row.removeAttribute('data-dragging');
      track.dispatchEvent(new Event('scroll'));
      // Swallow the click that follows the drag, then forget the drag.
      setTimeout(() => {
        moved = false;
      }, 0);
    };

    window.addEventListener('pointerup', stop, { signal });
    window.addEventListener('pointercancel', stop, { signal });

    track.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
      },
      { capture: true, signal }
    );
  }

  /** @param {HTMLElement} track */
  #canDrag(track) {
    return track.scrollWidth - track.clientWidth > 1;
  }

  /**
   * Moves the strip by roughly one screenful of cards.
   * @param {HTMLElement} row
   * @param {1 | -1} direction
   */
  #scrollRow(row, direction) {
    const track = row.querySelector('[data-track]');
    if (!(track instanceof HTMLElement)) return;

    const item = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = item instanceof HTMLElement ? item.offsetWidth + gap : track.clientWidth;
    const visible = Math.max(1, Math.floor((track.clientWidth + gap) / step));
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;

    track.scrollBy({ left: direction * rtl * step * visible, behavior: this.#scrollBehavior });
  }

  /**
   * @param {MouseEvent} event
   * @param {HTMLAnchorElement} link
   */
  #handleLinkClick(event, link) {
    const id = link.dataset.rowLink;
    const row = id ? document.getElementById(id) : null;
    if (!row) return;

    event.preventDefault();
    row.scrollIntoView({ behavior: this.#scrollBehavior, block: 'start' });
    history.replaceState(null, '', `#${id}`);
    this.#setCurrent(id);
  }

  /** @param {string | undefined} id */
  #setCurrent(id) {
    for (const link of this.links) {
      if (link.dataset.rowLink === id) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  /** @returns {ScrollBehavior} */
  get #scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }
}

if (!customElements.get('collection-rows')) {
  customElements.define('collection-rows', CollectionRows);
}
